import React, { useState, useRef, useEffect } from 'react';
import useSWR from 'swr';
import { useExpenses, createExpense, updateExpense, deleteExpense } from '../hooks/useExpenses';
import ExpenseTable from './Expenses/components/ExpenseTable';
import ExpenseFormDrawer from './Expenses/components/ExpenseFormDrawer';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';

const ExpensesPage: React.FC = () => {
  const { token, currentBusiness } = useAuth();
  const businessId = currentBusiness?.id || '';

  // Always call hooks, even if token/businessId are missing
  const { expenses, isLoading, isError, mutate } = useExpenses();
  const { data: categoriesData = [] } = useSWR(
    token && businessId ? ['/categories', token, businessId] : null,
    ([url, token, businessId]) =>
      fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      }).then(res => res.json())
  );
  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<any>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => addBtnRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  if (!token || !businessId) return null;

  const handleAdd = () => {
    console.log('Add Expense button clicked');
    setEditingExpense(null);
    setDrawerOpen(true);
  };

  const handleEdit = (expense: any) => {
    setEditingExpense(expense);
    setDrawerOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deleteExpense(businessId, id, token);
    mutate();
  };

  const handleSave = async (expense: any) => {
    if (editingExpense) {
      await updateExpense(businessId, editingExpense.id, expense, token);
    } else {
      await createExpense(businessId, expense, token);
    }
    setDrawerOpen(false);
    mutate();
  };

  const handleOpen = () => setDrawerOpen(true);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Expenses</h1>
        <Button
          ref={addBtnRef}
          onClick={handleOpen}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpen();
            }
          }}
          aria-label="Add Expense"
        >
          Add Expense
        </Button>
      </div>
      <ExpenseTable
        expenses={expenses}
        loading={isLoading}
        error={isError}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <ExpenseFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        expense={editingExpense}
        categories={categories}
      />
    </div>
  );
};

export default ExpensesPage; 